import React, { useEffect, useMemo, useState } from "react";
import { Column, useTable, Row } from "react-table";
import { useIntl } from "react-intl";
import moment from "moment";
import { useStateMachine } from "little-state-machine";
import { useNavigate } from "react-router-dom";

import { getQuickRequestsByIds } from "api";
import getLabel from "helpers/getLabel";
import DataTable from "components/organisms/DataTable/DataTable";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Badge from "components/atoms/Badge/Badge";
import { QuickRequestStatusColors } from "constants/";
import { updateShipmentIdAndTitle } from "store/store";

type ImportQuickListProps = {
  row: any;
};

function ImportQuickList(props: ImportQuickListProps) {
  const { row } = props;
  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const intl = useIntl();
  const { actions } = useStateMachine({ updateShipmentIdAndTitle });
  const navigate = useNavigate();

  useEffect(() => {
    if (!row?.original?.metadata?.imported_objects_references) {
      return;
    }
    setFetching(true);
    getQuickRequestsByIds({
      quick_request_references: row.original.metadata.imported_objects_references,
    })
      .then((res) => {
        setData(res.data);
      })
      .catch(() => {})
      .finally(() => {
        setFetching(false);
      });
  }, []);

  const columns = useMemo<Column[]>(
    () => [
      {
        id: "modified",
        Header: intl.formatMessage({
          id: "general.shipment",
          defaultMessage: "Shipment",
        }),
        accessor: "quick_request_id",
        Cell: ({ value, row }: any) => (
          <>
            <p className="mb-0 font-weight-bold">{value}</p>
            <span>{row.original?.title}</span>
          </>
        ),
      },
      {
        Header: intl.formatMessage(
          {
            id: "general.paymentLocation",
            defaultMessage: "PAYMENT LOCATION",
          },
          { paymentLocation: getLabel("Payment location", "capitalize") },
        ),
        accessor: "payment_location",
        Cell: ({ value, row }: any) => (
          <>
            <p className="mb-0">{value?.label || "-"}</p>
            <span className="text-primary mt-2">
              <OverlayTrigger
                placement="top"
                delay={{ show: 50, hide: 50 }}
                overlay={<Tooltip id="button-tooltip">{row.original?.created_by?.email}</Tooltip>}
              >
                <span>{row.original?.created_by?.name}</span>
              </OverlayTrigger>
            </span>
          </>
        ),
      },
      {
        id: "route",
        Header: intl.formatMessage({
          id: "quote.listing.fromTo",
          defaultMessage: "From-to",
        }),
        accessor: "pickup_city",
        Cell: ({ value, row }: any) => {
          if (!value && !row.original?.delivery_city) return <div className="mb-0">-</div>;
          return (
            <p className="mb-0">
              {value} - {row.original?.delivery_city}
            </p>
          );
        },
      },
      {
        Header: intl.formatMessage({
          id: "quote.listing.creationDate",
          defaultMessage: "Creation Date",
        }),
        accessor: "created",
        Cell: ({ value }: any) => {
          const creationDateTime = moment(value, "YYYY-MM-DD hh:mm:ss").format("DD MMM YYYY - HH:mm");
          return <p className="mb-0">{creationDateTime}</p>;
        },
      },
      {
        id: "status",
        Header: intl.formatMessage({
          id: "general.status",
          defaultMessage: "Status",
        }),
        accessor: "status",
        Cell: ({ value: cellValue }: any) =>
          cellValue && (
            <div className="d-flex flex-column align-items-start">
              <Badge type={QuickRequestStatusColors[cellValue] || "secondary"} label={cellValue} />
            </div>
          ),
      },
    ],
    [],
  );

  const instance = useTable({
    columns,
    data,
  });

  const handleRowClick = (row: Row<any>, e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    const reference = row.original?.quick_request_id;
    const title = row.original?.title;

    actions.updateShipmentIdAndTitle({
      shipmentId: reference,
      shipmentTitle: title,
    });

    if (e.ctrlKey || e.metaKey) window.open(`/shipment/${reference}/quick`, "_blank");
    else navigate(`/shipment/${reference}/quick`, { state: { data: row.original?.created_by } });
  };

  return (
    <div>
      <DataTable tableInstance={instance} draggable={false} isLoading={fetching} onRowClick={handleRowClick} />
    </div>
  );
}

export default ImportQuickList;
